/** Arrastar por teclado: uma seta, uma etapa.
 *
 * O `KeyboardSensor` do dnd-kit vem com um deslocamento fixo de 25 pixels por
 * tecla. Numa coluna de 300 px com 16 de intervalo, atravessar para a etapa ao
 * lado custa **treze toques** — e chegar em "Ganho", a partir de "Novo", passa
 * de cinquenta. Tecnicamente operável, praticamente inutilizável.
 *
 * O ADR-0016 adotou o dnd-kit justamente porque o requisito que decide não é
 * arrastar com o mouse, é arrastar **sem** ele. Um caminho de teclado que
 * ninguém aguenta percorrer não cumpre esse requisito — só o satisfaz no papel.
 *
 * Aqui a seta salta para o centro da coluna vizinha. A lógica fica separada do
 * componente porque é aritmética de retângulos, e aritmética se testa sem
 * montar tela nenhuma.
 */

import { KeyboardCode, defaultKeyboardCoordinateGetter } from "@dnd-kit/core";

import type { Announcements, KeyboardCoordinateGetter } from "@dnd-kit/core";

import type { Coluna, Quadro } from "./api";

/** O mínimo de um retângulo que este cálculo precisa. */
export interface Retangulo {
  left: number;
  width: number;
}

export interface PedidoDeSalto {
  /** `-1` para a esquerda, `1` para a direita. */
  direcao: -1 | 1;
  /** Onde o card está agora. */
  retanguloAtual: Retangulo;
  /** As colunas, em qualquer ordem — a função ordena. */
  retangulosDasEtapas: Retangulo[];
  /** A coordenada que o sensor mantém, e que a devolução ajusta. */
  coordenadaAtual: { x: number; y: number };
}

function centro(retangulo: Retangulo): number {
  return retangulo.left + retangulo.width / 2;
}

/** A coordenada que põe o card sobre a coluna vizinha, ou `null` se não há.
 *
 * `null` na ponta é deliberado: o card **não se move**, e o leitor de tela não
 * anuncia nada. Deslizar alguns pixels contra a borda seria pior — dá a
 * impressão de que a tecla funcionou e o card ficou preso.
 */
export function coordenadaDaEtapaVizinha(pedido: PedidoDeSalto): { x: number; y: number } | null {
  const { direcao, retanguloAtual, retangulosDasEtapas, coordenadaAtual } = pedido;
  if (retangulosDasEtapas.length === 0) return null;

  const ordenadas = [...retangulosDasEtapas].sort((a, b) => a.left - b.left);
  const centroAtual = centro(retanguloAtual);

  // A coluna de origem é a mais próxima do centro do card, e não a que o
  // contém: durante o arrasto o card passa por cima do intervalo entre duas
  // colunas, onde nenhuma o contém e a busca por continência devolveria nada.
  let indiceAtual = 0;
  let menorDistancia = Number.POSITIVE_INFINITY;
  ordenadas.forEach((etapa, indice) => {
    const distancia = Math.abs(centro(etapa) - centroAtual);
    if (distancia < menorDistancia) {
      menorDistancia = distancia;
      indiceAtual = indice;
    }
  });

  const destino = ordenadas[indiceAtual + direcao];
  if (destino === undefined) return null;

  return {
    x: coordenadaAtual.x + (centro(destino) - centroAtual),
    y: coordenadaAtual.y,
  };
}

/** O que o `KeyboardSensor` recebe.
 *
 * Setas horizontais saltam de etapa; o resto continua com o comportamento
 * padrão da biblioteca — o vertical percorre cards dentro da coluna, e ali os
 * 25 px fazem sentido.
 */
export const saltarEntreEtapas: KeyboardCoordinateGetter = (evento, argumentos) => {
  // A conversão para `KeyboardCode` é o que permite comparar com as constantes
  // da biblioteca em vez de com literais soltos: `evento.code` é `string`, e
  // comparar `string` com enum é o que o `no-unsafe-enum-comparison` barra —
  // com razão, porque esconde erro de digitação.
  const codigo = evento.code as KeyboardCode;
  const horizontal = codigo === KeyboardCode.Right ? 1 : codigo === KeyboardCode.Left ? -1 : 0;

  if (horizontal === 0) return defaultKeyboardCoordinateGetter(evento, argumentos);

  const { context, currentCoordinates } = argumentos;
  const atual = context.collisionRect;
  if (atual == null) return defaultKeyboardCoordinateGetter(evento, argumentos);

  const etapas: Retangulo[] = [];
  context.droppableContainers.forEach((container) => {
    const retangulo = context.droppableRects.get(container.id);
    if (retangulo !== undefined) etapas.push(retangulo);
  });

  const destino = coordenadaDaEtapaVizinha({
    direcao: horizontal,
    retanguloAtual: atual,
    retangulosDasEtapas: etapas,
    coordenadaAtual: currentCoordinates,
  });

  // `undefined` diz ao sensor para não mover — é o que se quer na ponta do
  // funil, e o `void` do contrato da biblioteca.
  return destino ?? undefined;
};

/** O nome da etapa sob o card, para o anúncio. */
function nomeDaEtapa(over: { data: { current?: Record<string, unknown> } }): string {
  const coluna = over.data.current?.coluna as Coluna | undefined;
  return coluna?.stage_name ?? "outra etapa";
}

/** O título do card, para o anúncio de início do gesto.
 *
 * O `id` do arrasto é o da oportunidade, e ele não serve para ser lido em voz
 * alta: o anúncio soletrava trinta e seis caracteres de UUID e não dizia qual
 * card a pessoa acabou de pegar.
 */
export function tituloDoCard(quadro: Quadro | undefined, cardId: string): string {
  for (const coluna of quadro?.columns ?? []) {
    const card = coluna.cards.find((c) => c.id === cardId);
    if (card !== undefined) return card.title;
  }
  return "selecionada";
}

/** O que o leitor de tela ouve durante o gesto, em português.
 *
 * Os anúncios da biblioteca são em inglês, e um board em português que fala
 * "Draggable item was moved" é pior que o silêncio (ADR-0016).
 *
 * Fábrica, e não objeto embutido no componente: assim as quatro frases se
 * testam sem montar tela nem simular arrasto — e foi o que faltou da primeira
 * vez, quando o teste do título passava mesmo com o UUID de volta no anúncio.
 */
export function anunciosDoArrasto(quadro: Quadro | undefined): Announcements {
  return {
    onDragStart: ({ active }) => `Pegou a oportunidade ${tituloDoCard(quadro, String(active.id))}.`,
    onDragOver: ({ over }) =>
      over == null ? "Fora de qualquer etapa." : `Sobre a etapa ${nomeDaEtapa(over)}.`,
    onDragEnd: ({ over }) =>
      over == null ? "Solto fora de uma etapa. Nada mudou." : `Movido para ${nomeDaEtapa(over)}.`,
    onDragCancel: () => "Movimentação cancelada. O card voltou para onde estava.",
  };
}
